import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { signIn } from "../../lib/cognito";

export default function LoginScreen() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [agreedToPrivacy, setAgreedToPrivacy] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();

    if (!agreedToTerms || !agreedToPrivacy) {
      setError(
        "You must agree to the Terms of Service and Privacy Policy before signing in."
      );
      return;
    }

    try {
      await signIn({ username: email, password });
      window.location.href = "/dashboard";
    } catch (err) {
      setError(err.message || "Login failed");
    }
  };

  const isDisabled = !agreedToTerms || !agreedToPrivacy;

  return (
    <div className="relative min-h-screen overflow-hidden bg-[url('/assets/images/medical.jpg')] bg-cover bg-center bg-no-repeat bg-fixed flex flex-col">
      {/* Premium dark overlay */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,_rgba(15,23,42,0.85),rgba(15,23,42,0.97))]" />

      {/* Ambient glows */}
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute -top-24 -left-10 h-64 w-64 rounded-full bg-cyan-300/20 blur-3xl" />
        <div className="absolute bottom-0 right-0 h-80 w-80 rounded-full bg-sky-400/20 blur-3xl" />
      </div>

      {/* CONTENT */}
      <div className="relative z-10 flex flex-col min-h-screen">
        
        {/* HEADER — logo + text (bigger, rounded corners) */}
        <header className="px-4 pt-4 sm:px-8 sm:pt-6">
          <button
            onClick={() => navigate("/")}
            className="inline-flex items-center gap-4 rounded-2xl bg-black/10 px-4 py-3 backdrop-blur-xl border border-white/10 hover:border-white/30 transition-all"
          >
            <img
              src="/assets/images/graftcounter-logo.png"
              alt="Graft Counter Logo"
              className="h-9 w-9 rounded-xl object-contain"  // bigger + rounded
            />
            <span className="text-[15px] font-semibold tracking-wide text-slate-200">
              Graft Counter
            </span>
          </button>
        </header>

        {/* CENTER LOGIN CARD */}
        <main className="flex flex-1 items-center justify-center px-4 pb-10 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 16, scale: 0.98, filter: "blur(4px)" }}
            animate={{ opacity: 1, y: 0, scale: 1, filter: "blur(0px)" }}
            transition={{ duration: 0.45, ease: "easeOut" }}
            className="w-full max-w-md rounded-[28px] bg-white/5 border border-white/15 shadow-[0_28px_90px_rgba(0,0,0,0.7)] backdrop-blur-2xl px-6 py-8 sm:px-10 sm:py-10"
          >
            
            {/* BRANDING — logo (bigger, rounded) */}
            <div className="mb-8 text-center">
              <img
                src="/assets/images/graftcounter-logo.png"
                alt="Graft Counter Logo"
                className="h-20 w-20 mx-auto mb-5 object-contain rounded-2xl"  // bigger + rounded smoothly
              />
              <h1 className="text-[26px] font-semibold tracking-tight text-white">
                Sign in to Graft Counter
              </h1>
            </div>

            {/* FORM */}
            <form onSubmit={handleLogin} className="space-y-5">
              {/* EMAIL */}
              <div>
                <label className="block text-xs font-medium text-slate-200/90">
                  Email
                </label>
                <div className="mt-1.5 rounded-2xl border border-white/15 bg-white/5 px-3 py-2.5 shadow-inner shadow-black/20 focus-within:border-sky-300/70 focus-within:bg-white/10 focus-within:shadow-[0_0_0_1px_rgba(125,211,252,0.8)] transition-all">
                  <input
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@clinic.com"
                    className="w-full bg-transparent text-sm text-slate-50 placeholder:text-slate-400 outline-none"
                  />
                </div>
              </div>

              {/* PASSWORD */}
              <div>
                <label className="block text-xs font-medium text-slate-200/90">
                  Password
                </label>
                <div className="mt-1.5 rounded-2xl border border-white/15 bg-white/5 px-3 py-2.5 shadow-inner shadow-black/20 focus-within:border-sky-300/70 focus-within:bg-white/10 focus-within:shadow-[0_0_0_1px_rgba(125,211,252,0.8)] transition-all">
                  <input
                    type="password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    className="w-full bg-transparent text-sm text-slate-50 placeholder:text-slate-400 outline-none"
                  />
                </div>
              </div>

              {/* TERMS + PRIVACY */}
              <div className="mt-3 space-y-2.5 text-[11px] text-slate-300">
                <label className="flex items-start gap-2">
                  <input
                    type="checkbox"
                    checked={agreedToTerms}
                    onChange={(e) => setAgreedToTerms(e.target.checked)}
                    className="mt-0.5 h-4 w-4 rounded border-white/30 bg-black/20 text-sky-300 focus:ring-sky-300/60"
                  />
                  <span>
                    I agree to the{" "}
                    <button
                      type="button"
                      onClick={() => setShowTermsModal(true)}
                      className="underline decoration-sky-300/60 underline-offset-2 text-sky-200 hover:text-sky-100"
                    >
                      Terms of Service
                    </button>
                    .
                  </span>
                </label>

                <label className="flex items-start gap-2">
                  <input
                    type="checkbox"
                    checked={agreedToPrivacy}
                    onChange={(e) => setAgreedToPrivacy(e.target.checked)}
                    className="mt-0.5 h-4 w-4 rounded border-white/30 bg-black/20 text-sky-300 focus:ring-sky-300/60"
                  />
                  <span>
                    I agree to the{" "}
                    <button
                      type="button"
                      onClick={() => setShowPrivacyModal(true)}
                      className="underline decoration-sky-300/60 underline-offset-2 text-sky-200 hover:text-sky-100"
                    >
                      Privacy Policy
                    </button>
                    .
                  </span>
                </label>
              </div>

              {/* ERRORS */}
              {error && (
                <div className="mt-2 rounded-2xl border border-rose-500/40 bg-rose-500/10 px-3 py-2 text-center text-[11px] text-rose-100">
                  {error}
                </div>
              )}

              {/* SIGN IN BUTTON */}
              <button
                type="submit"
                disabled={isDisabled}
                className={`w-full rounded-2xl px-4 py-3 text-sm font-medium tracking-tight transition-all duration-200 ${
                  isDisabled
                    ? "cursor-not-allowed border border-white/10 bg-white/5 text-slate-500/60"
                    : "border border-transparent bg-white text-slate-950 shadow-[0_20px_50px_rgba(15,23,42,0.85)] hover:bg-slate-100 active:scale-[0.99]"
                }`}
              >
                Sign in
              </button>
            </form>
          </motion.div>
        </main>
      </div>

      {/* TERMS MODAL — FULL TEXT INCLUDED */}
      {showTermsModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
          onClick={() => setShowTermsModal(false)}
        >
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 0.35 }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-3xl mx-4 rounded-3xl border border-white/15 bg-slate-950/90 px-6 py-6 sm:px-8 sm:py-8 shadow-[0_32px_90px_rgba(0,0,0,0.9)]"
          >
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-white">Terms of Service</h2>
              <button
                onClick={() => setShowTermsModal(false)}
                className="rounded-full bg-white/5 px-3 py-1 text-xs text-slate-200 hover:bg-white/10 transition-colors"
              >
                Close
              </button>
            </div>

            <div className="max-h-[70vh] overflow-y-auto pr-2 whitespace-pre-wrap text-[13px] leading-relaxed text-slate-100/90">
{`🧾 Terms of Service
Last Updated: November 2025
Welcome to Graft Counter (“the Service”), an AI-powered graft counting and density analysis platform (“we,” “our,” “us”).
By creating an account or using our service, you (“User”) agree to be bound by these Terms of Service.
If you do not agree, please do not use the Service.

1. PURPOSE OF THE SERVICE
Graft Counter provides automated graft detection, density mapping, and visual reporting based on user-uploaded images from hair transplantation procedures.
The Service is intended for informational and analytical purposes only and is not a substitute for professional medical judgment.

2. ACCOUNT REGISTRATION & ACCESS
You must be at least 18 years old and authorized to upload patient images.
You are responsible for your account security.

3. UPLOADED IMAGES AND DATA QUALITY
Accuracy depends on image quality.
We are not liable for inaccurate results caused by low-resolution or non-standard images.

4. AI MODEL & CALIBRATION NOTICE
The model uses deep learning and may over/under-estimate graft counts.
A calibration adjustment is applied to align model behavior with internal benchmarks.

5. LIMITATION OF LIABILITY
The Service is provided “as is.”
We do not guarantee perfect accuracy.

6. INTELLECTUAL PROPERTY
All algorithms, models, and assets belong to Graft Counter.

7. SERVICE AVAILABILITY & CHANGES
We may update, modify, or suspend the Service at any time.

8. TERMINATION
We may terminate accounts that violate these Terms.

9. DISCLAIMER OF WARRANTIES
This is not medical advice.

10. GOVERNING LAW
These Terms are governed by applicable laws.

11. CONTACT
For questions, contact us through the platform.`}
            </div>
          </motion.div>
        </div>
      )}

      {/* PRIVACY MODAL — FULL TEXT INCLUDED */}
      {showPrivacyModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
          onClick={() => setShowPrivacyModal(false)}
        >
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 0.35 }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-3xl mx-4 rounded-3xl border border-white/15 bg-slate-950/90 px-6 py-6 sm:px-8 sm:py-8 shadow-[0_32px_90px_rgba(0,0,0,0.9)]"
          >
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-white">Privacy Policy</h2>
              <button
                onClick={() => setShowPrivacyModal(false)}
                className="rounded-full bg-white/5 px-3 py-1 text-xs text-slate-200 hover:bg-white/10 transition-colors"
              >
                Close
              </button>
            </div>

            <div className="max-h-[70vh] overflow-y-auto pr-2 whitespace-pre-wrap text-[13px] leading-relaxed text-slate-100/90">
{`🔒 Privacy Policy
Last Updated: November 2025

1. Uploaded Images
Images are processed temporarily only. They are not stored permanently and are deleted automatically after analysis.

2. Data Responsibility
Users are responsible for patient consent and data protection compliance.

3. Security
All communication is encrypted end-to-end.

4. Demo Disclaimer
As a demo version, results are for informational purposes only.

5. Updates
We may update this Policy at any time.

6. Contact
For questions about privacy, contact us via the platform.`}
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
}
